<template>
    <div class="channel-form">
        <div class="steps-wizard">
            <button 
                v-for="(step, index) in steps" 
                :key="step.id"
                @click="handleStepClick(step.id)"
                :class="getStepClasses(step, index)"
                :disabled="isStepDisabled(step, index)"
            >
                <div class="icon-holder">
                    <Icon :icon-class="step.icon" size="4" />
                </div>
                <span>{{ step.label }}</span>
            </button>
        </div>

        <div class="steps-content">
            <!-- Default slot for step components -->
            <slot 
                :current-step="currentStep"
                :next-step="nextStep"
                :previous-step="previousStep"
                :set-step="setStep"
                :close="close"
            />
        </div>
    </div>
</template>

<script>
export default {
    name: 'ChannelForm',
    props: {
        /**
         * Steps configuration array
         * Each step: { id: string, label: string, icon: string, disabled?: boolean, requiresConnection?: boolean }
         */
        steps: {
            type: Array,
            required: true,
        },
        /**
         * Current active step id
         */
        currentStep: {
            type: String,
            required: true,
        },
        /**
         * Connection status for steps that depend on it (optional)
         */
        connectionStatus: {
            type: String,
            default: null,
        },
    },
    computed: {
        stepOrder() {
            return this.steps.map(s => s.id);
        },
        currentStepIndex() {
            return this.stepOrder.indexOf(this.currentStep);
        },
    },
    methods: {
        getStepClasses(step, index) {
            const currentIndex = this.currentStepIndex;
            const isActive = this.currentStep === step.id;
            const isPast = index < currentIndex;
            const isFuture = index > currentIndex;
            const isFinished = this.currentStep === 'finished';
            
            // Special case for connection-dependent steps
            const isConnectedStep = step.requiresConnection && this.connectionStatus === 'connected';
            
            return {
                active: isActive && !(isFinished && step.id === 'finished'),
                success: isPast || (isFinished && step.id === 'finished') || isConnectedStep,
                disabled: step.disabled || (isFuture && !isConnectedStep),
            };
        },
        isStepDisabled(step, index) {
            if (step.disabled) return true;
            // Allow clicking on past steps
            if (index < this.currentStepIndex) return false;
            // For future steps, check if they have special requirements
            if (step.requiresConnection && this.connectionStatus !== 'connected') return true;
            return false;
        },
        handleStepClick(stepId) {
            const clickedIndex = this.stepOrder.indexOf(stepId);
            // Only allow clicking on past steps or current step
            if (clickedIndex <= this.currentStepIndex) {
                this.setStep(stepId);
            }
        },
        setStep(stepId) {
            if (this.stepOrder.includes(stepId)) {
                this.$emit('update:currentStep', stepId);
                this.$emit('step-change', stepId);
            }
        },
        nextStep() {
            const nextIndex = this.currentStepIndex + 1;
            if (nextIndex < this.steps.length) {
                this.setStep(this.steps[nextIndex].id);
            }
        },
        previousStep() {
            const prevIndex = this.currentStepIndex - 1;
            if (prevIndex >= 0) {
                this.setStep(this.steps[prevIndex].id);
            }
        },
        close() {
            this.$root.$emit('close-drawer');
        },
    },
};
</script>

<style scoped lang="less">
.channel-form {
    .box {
        padding: 20px;
    }

    .steps-wizard {
        display: flex;
        gap: 24px;
        text-align: center;
        justify-content: center;

        button {
            background: none;
            border: none;
            cursor: pointer;
            padding: 0;
            margin: 0;
            font-size: 14px;
            font-weight: 600;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;

            &[disabled] {
                cursor: not-allowed;
                pointer-events: none;
            }
        }

        .icon-holder {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            border-radius: 50%;
        }

        span {
            font-size: 14px;
            font-weight: 600;
        }

        .success {
            .icon-holder {
                background: #D3F8E0;
                color: #17B26A;
            }
        }

        .active {
            .icon-holder {
                background: #E5E7EB;
                color: #4D5761;
            }
        }

        .disabled {
            .icon-holder {
                color: #9DA4AE;
            }
            color: #D2D6DB;
        }
    }

    .steps-content {
        margin-top: 16px;
    }
}
</style>
